import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import actionDeleteIconUrl from '@/assets/action-menu/delete.svg'
import actionMembersIconUrl from '@/assets/action-menu/members.svg'
import actionProbeIconUrl from '@/assets/action-menu/probe.svg'
import { Switch } from '@/components/forms/switch'
import actionEditIconUrl from '@/assets/action-menu/edit.svg'
import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { ErrorState } from '@/components/feedback/error-state'
import { useFeedback } from '@/components/feedback/feedback-context'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { PasswordInput } from '@/components/forms/password-input'
import { PageHeader } from '@/components/layout/page-header'
import { ActionMenu } from '@/components/overlays/action-menu'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { ApiError } from '@/lib/api/http-client'
import { readStoredPortalAccount } from '@/lib/auth/session-storage'

import {
  createWecomIdentityProfile,
  deleteWecomIdentityProfile,
  listWecomIdentityProfiles,
  rotateWecomIdentityProfileSecret,
  setWecomIdentityProfileStatus,
  updateWecomIdentityProfile,
  validateWecomIdentityProfile,
  validateWecomIdentityProfileDraft,
  wecomIdentityProfilesQueryKey,
  type WecomIdentityProfile,
  type WecomIdentityProfileCreatePayload,
  type WecomIdentityProfileUpdatePayload,
  type WecomIdentityReadiness,
  type WecomIdentityReadinessStatus,
} from './wecom-identity-profiles-api'

import { WecomEntryLink } from './wecom-entry-link'
import { WecomEmailBindingPanel } from './wecom-email-binding-panel'

interface ProfileFormState {
  agentId: string
  appSecret: string
  corpId: string
  displayName: string
}

type ProfileFormErrors = Partial<Record<keyof ProfileFormState, string>>

type ProfileDialogState =
  | { mode: 'create'; profile?: undefined }
  | { mode: 'edit'; profile: WecomIdentityProfile }

function readFormState(profile?: WecomIdentityProfile): ProfileFormState {
  return {
    agentId: profile?.agent_id ?? '',
    appSecret: '',
    corpId: profile?.corp_id ?? profile?.corp_id_masked ?? '',
    displayName: profile?.display_name ?? '',
  }
}

function buildCreatePayload(state: ProfileFormState): WecomIdentityProfileCreatePayload {
  return {
    agent_id: state.agentId.trim(),
    app_secret: state.appSecret.trim(),
    corp_id: state.corpId.trim(),
    display_name: state.displayName.trim(),
  }
}

function buildUpdatePayload(state: ProfileFormState): WecomIdentityProfileUpdatePayload {
  return {
    display_name: state.displayName.trim(),
  }
}

function readinessVariant(status: WecomIdentityReadinessStatus) {
  return status === 'ready' ? 'success' : status === 'blocked' ? 'danger' : 'warning'
}

export function AdminWecomIdentityProfilesPage() {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [dialogState, setDialogState] = useState<ProfileDialogState | null>(null)
  const [emailTarget, setEmailTarget] = useState<WecomIdentityProfile | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<WecomIdentityProfile | null>(null)
  const isSystemAdmin = readStoredPortalAccount('admin').role === 'super_admin'

  const profilesQuery = useQuery({
    enabled: isSystemAdmin,
    queryFn: listWecomIdentityProfiles,
    queryKey: wecomIdentityProfilesQueryKey,
  })

  const refreshProfiles = async () => {
    await queryClient.invalidateQueries({ queryKey: wecomIdentityProfilesQueryKey })
    await queryClient.invalidateQueries({ queryKey: ['admin-app-wecom-identity-profiles'] })
  }

  const createMutation = useMutation({
    mutationFn: createWecomIdentityProfile,
    onError: () =>
      showFeedback({ message: t('wecomIdentityProfiles.feedback.createFailed'), tone: 'error' }),
    onSuccess: async () => {
      setDialogState(null)
      await refreshProfiles()
      showFeedback({ message: t('wecomIdentityProfiles.feedback.createSuccess'), tone: 'success' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({
      appSecret,
      expectedSecretVersion,
      enabled,
      profileId,
      values,
    }: {
      appSecret: string
      expectedSecretVersion: number
      enabled: boolean | undefined
      profileId: string
      values: WecomIdentityProfileUpdatePayload
    }) => {
      await updateWecomIdentityProfile({ profileId, values })
      if (appSecret.trim()) {
        await rotateWecomIdentityProfileSecret({
          appSecret: appSecret.trim(),
          expectedSecretVersion,
          profileId,
        })
      }
      if (enabled !== undefined) await setWecomIdentityProfileStatus({ profileId, enabled })
    },
    onError: async (error) => {
      const secretVersionStale =
        error instanceof ApiError &&
        (error.status === 409 || error.errorCode === 'im.identity_profile.secret_version_stale')
      if (secretVersionStale) {
        setDialogState(null)
        await refreshProfiles()
        showFeedback({
          message: t('wecomIdentityProfiles.feedback.secretVersionStale'),
          tone: 'error',
        })
        return
      }
      showFeedback({ message: t('wecomIdentityProfiles.feedback.updateFailed'), tone: 'error' })
    },
    onSuccess: async () => {
      setDialogState(null)
      await refreshProfiles()
      showFeedback({ message: t('wecomIdentityProfiles.feedback.updateSuccess'), tone: 'success' })
    },
  })

  const validateMutation = useMutation({
    mutationFn: (profileId: string) => validateWecomIdentityProfile(profileId),
    onError: () =>
      showFeedback({ message: t('wecomIdentityProfiles.feedback.validateFailed'), tone: 'error' }),
    onSuccess: async (readiness) => {
      await refreshProfiles()
      showFeedback({
        detail: t(`wecomIdentityProfiles.readiness.description.${readiness.status}`),
        message: t('wecomIdentityProfiles.feedback.validateComplete'),
        tone:
          readiness.status === 'blocked'
            ? 'error'
            : readiness.status === 'ready'
              ? 'success'
              : 'warning',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteWecomIdentityProfile,
    onError: () =>
      showFeedback({ message: t('wecomIdentityProfiles.feedback.deleteFailed'), tone: 'error' }),
    onSuccess: async () => {
      setDeleteTarget(null)
      await refreshProfiles()
      showFeedback({ message: t('wecomIdentityProfiles.feedback.deleteSuccess'), tone: 'success' })
    },
  })

  const rows = profilesQuery.data ?? []
  const columns = useMemo<DataTableColumn<WecomIdentityProfile>[]>(
    () => [
      {
        header: t('wecomIdentityProfiles.table.name'),
        id: 'name',
        render: (profile) => <span className="font-medium">{profile.display_name}</span>,
        width: 280,
      },
      {
        header: t('wecomIdentityProfiles.table.connection'),
        id: 'connection',
        render: (profile) =>
          profile.readiness.status === 'unknown' ? (
            <span className="text-[var(--color-text-subtle)]">—</span>
          ) : (
            <Badge variant={readinessVariant(profile.readiness.status)}>
              {t(`wecomIdentityProfiles.connection.${profile.readiness.status}`)}
            </Badge>
          ),
        width: 150,
      },
      {
        header: t('wecomIdentityProfiles.table.references'),
        id: 'references',
        render: (profile) =>
          t('wecomIdentityProfiles.references.summary', {
            bindings: profile.active_binding_count,
            integrations: profile.integration_ref_count,
          }),
        width: 190,
      },
      {
        header: t('wecomIdentityProfiles.table.status'),
        id: 'status',
        render: (profile) => (
          <Badge variant={profile.status === 'active' ? 'success' : 'neutral'}>
            {t(`wecomIdentityProfiles.status.${profile.status}`)}
          </Badge>
        ),
        width: 110,
      },
      {
        align: 'center',
        header: t('wecomIdentityProfiles.table.actions'),
        id: 'actions',
        pin: 'right',
        render: (profile) => {
          const hasReferences =
            profile.integration_ref_count > 0 || profile.active_binding_count > 0
          return (
            <ActionMenu
              ariaLabel={profile.display_name}
              items={[
                {
                  iconSrc: actionMembersIconUrl,
                  key: 'email',
                  label: t('wecomEmail.title'),
                  onSelect: () => setEmailTarget(profile),
                },
                {
                  iconSrc: actionEditIconUrl,
                  key: 'edit',
                  label: t('actions.edit'),
                  onSelect: () => setDialogState({ mode: 'edit', profile }),
                },
                {
                  iconSrc: actionProbeIconUrl,
                  key: 'validate',
                  label: t('wecomIdentityProfiles.actions.validate'),
                  onSelect: () => validateMutation.mutate(profile.id),
                },
                {
                  disabled: hasReferences,
                  iconSrc: actionDeleteIconUrl,
                  key: 'delete',
                  label: t('actions.delete'),
                  onSelect: () => setDeleteTarget(profile),
                  tooltip: hasReferences
                    ? t('wecomIdentityProfiles.actions.deleteReferenced')
                    : undefined,
                  variant: 'danger',
                },
              ]}
            />
          )
        },
        width: 90,
      },
    ],
    [t, validateMutation],
  )

  return (
    <AdminShellPage activePath="/admin/credentials/wecom">
      <section
        aria-label={t('wecomIdentityProfiles.workspaceLabel')}
        className="flex h-full min-w-0 flex-col overflow-hidden bg-white"
        data-testid="admin-wecom-identity-profiles-workspace"
      >
        <PageHeader
          actions={
            isSystemAdmin ? (
              <Button onClick={() => setDialogState({ mode: 'create' })} size="sm">
                <PlusIcon />
                {t('wecomIdentityProfiles.actions.create')}
              </Button>
            ) : null
          }
          description={t('wecomIdentityProfiles.description')}
          title={t('wecomIdentityProfiles.title')}
        />
        <div className="min-h-0 flex-1 overflow-auto px-5 py-5 lg:px-[30px]">
          {!isSystemAdmin ? (
            <ErrorState
              description={t('wecomIdentityProfiles.permissionDescription')}
              title={t('wecomIdentityProfiles.permissionTitle')}
            />
          ) : profilesQuery.isError ? (
            <ErrorState
              action={
                <Button onClick={() => void profilesQuery.refetch()} size="sm" variant="secondary">
                  {t('actions.retry')}
                </Button>
              }
              description={t('wecomIdentityProfiles.loadDescription')}
              title={t('wecomIdentityProfiles.loadTitle')}
            />
          ) : (
            <DataTable
              columns={columns}
              empty={t('wecomIdentityProfiles.empty')}
              getRowKey={(profile) => profile.id}
              isLoading={profilesQuery.isLoading}
              rows={rows}
            />
          )}
        </div>

        {emailTarget ? (
          <WecomEmailBindingPanel
            key={emailTarget.id}
            profile={emailTarget}
            onClose={() => setEmailTarget(null)}
          />
        ) : null}
        {dialogState ? (
          <WecomIdentityProfileModal
            isSubmitting={createMutation.isPending || updateMutation.isPending}
            key={dialogState.mode === 'edit' ? dialogState.profile.id : 'create'}
            onClose={() => setDialogState(null)}
            onCreate={(payload) => createMutation.mutate(payload)}
            onUpdate={(profileId, values, appSecret, expectedSecretVersion, enabled) =>
              updateMutation.mutate({
                appSecret,
                expectedSecretVersion,
                enabled,
                profileId,
                values,
              })
            }
            open
            profile={dialogState.profile}
          />
        ) : null}

        <Modal
          closeLabel={t('wecomIdentityProfiles.deleteDialog.close')}
          footer={
            <>
              <Button onClick={() => setDeleteTarget(null)} size="sm" variant="secondary">
                {t('actions.cancel')}
              </Button>
              <Button
                disabled={!deleteTarget}
                isLoading={deleteMutation.isPending}
                onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                size="sm"
                variant="danger"
              >
                {t('wecomIdentityProfiles.deleteDialog.confirm')}
              </Button>
            </>
          }
          onClose={() => setDeleteTarget(null)}
          open={Boolean(deleteTarget)}
          title={t('wecomIdentityProfiles.deleteDialog.title')}
        >
          <p className="text-sm leading-6 text-[var(--color-text)]">
            {t('wecomIdentityProfiles.deleteDialog.message', {
              name: deleteTarget?.display_name ?? '',
            })}
          </p>
        </Modal>
      </section>
    </AdminShellPage>
  )
}

function WecomIdentityProfileModal({
  isSubmitting,
  onClose,
  onCreate,
  onUpdate,
  open,
  profile,
}: {
  isSubmitting: boolean
  onClose: () => void
  onCreate: (payload: WecomIdentityProfileCreatePayload) => void
  onUpdate: (
    profileId: string,
    payload: WecomIdentityProfileUpdatePayload,
    appSecret: string,
    expectedSecretVersion: number,
    enabled: boolean | undefined,
  ) => void
  open: boolean
  profile?: WecomIdentityProfile
}) {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()
  const [formState, setFormState] = useState(() => readFormState(profile))
  const [enabled, setEnabled] = useState(profile?.status === 'active')
  const [errors, setErrors] = useState<ProfileFormErrors>({})
  const [validation, setValidation] = useState<WecomIdentityReadiness | null>(null)
  const validateMutation = useMutation({
    mutationFn: () =>
      profile
        ? validateWecomIdentityProfile(profile.id, formState.appSecret)
        : validateWecomIdentityProfileDraft(buildCreatePayload(formState)),
    onError: () =>
      showFeedback({ message: t('wecomIdentityProfiles.feedback.validateFailed'), tone: 'error' }),
    onSuccess: (result) => {
      setValidation(result)
      showFeedback({
        message: t('wecomIdentityProfiles.feedback.validateComplete'),
        tone:
          result.status === 'blocked' ? 'error' : result.status === 'ready' ? 'success' : 'warning',
      })
    },
  })

  const updateValue = <Key extends keyof ProfileFormState>(
    key: Key,
    value: ProfileFormState[Key],
  ) => {
    setFormState((current) => ({ ...current, [key]: value }))
    setErrors((current) => ({ ...current, [key]: undefined }))
    setValidation(null)
  }

  const validateForm = () => {
    const next: ProfileFormErrors = {}
    if (!formState.displayName.trim()) next.displayName = t('wecomIdentityProfiles.validation.name')
    if (!formState.corpId.trim()) next.corpId = t('wecomIdentityProfiles.validation.corpId')
    if (!formState.agentId.trim()) next.agentId = t('wecomIdentityProfiles.validation.agentId')
    if (!profile && !formState.appSecret.trim()) {
      next.appSecret = t('wecomIdentityProfiles.validation.appSecret')
    }
    setErrors(next)
    const firstError = Object.keys(next)[0]
    if (firstError) {
      window.requestAnimationFrame(() =>
        document.getElementById(`wecom-profile-${firstError}`)?.focus(),
      )
    }
    return Object.keys(next).length === 0
  }

  const submit = () => {
    if (!validateForm()) return
    if (profile) {
      onUpdate(
        profile.id,
        buildUpdatePayload(formState),
        formState.appSecret,
        profile.secret_version,
        enabled !== (profile.status === 'active') ? enabled : undefined,
      )
    } else {
      onCreate(buildCreatePayload(formState))
    }
  }

  const isBusy = isSubmitting || validateMutation.isPending

  return (
    <Modal
      className="!max-w-[680px] max-h-[calc(100dvh-48px)] grid-rows-[auto_minmax(0,1fr)_auto]"
      bodyClassName="min-h-0 overflow-y-auto"
      closeLabel={t('wecomIdentityProfiles.form.close')}
      footer={
        <>
          <Button
            className="mr-auto"
            disabled={isBusy}
            isLoading={validateMutation.isPending}
            onClick={() => {
              if (validateForm()) validateMutation.mutate()
            }}
            size="sm"
            variant="soft"
          >
            {t('wecomIdentityProfiles.actions.validate')}
          </Button>
          <Button disabled={isBusy} onClick={onClose} size="sm" variant="secondary">
            {t('actions.cancel')}
          </Button>
          <Button disabled={isBusy} isLoading={isSubmitting} onClick={submit} size="sm">
            {profile
              ? t('wecomIdentityProfiles.form.update')
              : t('wecomIdentityProfiles.form.create')}
          </Button>
        </>
      }
      onClose={() => !isBusy && onClose()}
      open={open}
      subtitle={profile ? undefined : t('wecomIdentityProfiles.form.createSubtitle')}
      title={
        profile
          ? t('wecomIdentityProfiles.form.editTitle', { name: profile.display_name })
          : t('wecomIdentityProfiles.form.createTitle')
      }
    >
      <div className="grid gap-4">
        {profile ? (
          <label className="flex items-center justify-between gap-3 text-sm font-semibold">
            {t('wecomIdentityProfiles.entry.enabled')}
            <Switch
              aria-label={t('wecomIdentityProfiles.entry.enabled')}
              checked={enabled}
              disabled={isBusy}
              onCheckedChange={setEnabled}
            />
          </label>
        ) : null}
        <ProfileField
          error={errors.displayName}
          htmlFor="wecom-profile-displayName"
          label={t('wecomIdentityProfiles.form.fields.name')}
          required
        >
          <Input
            id="wecom-profile-displayName"
            invalid={Boolean(errors.displayName)}
            onChange={(event) => updateValue('displayName', event.target.value)}
            placeholder={t('wecomIdentityProfiles.form.placeholders.name')}
            value={formState.displayName}
          />
        </ProfileField>
        <ProfileField
          error={errors.corpId}
          hint={profile ? t('wecomIdentityProfiles.form.identityImmutable') : undefined}
          htmlFor="wecom-profile-corpId"
          label={t('wecomIdentityProfiles.form.fields.corpId')}
          required
        >
          <Input
            disabled={Boolean(profile)}
            id="wecom-profile-corpId"
            invalid={Boolean(errors.corpId)}
            onChange={(event) => updateValue('corpId', event.target.value)}
            placeholder={t('wecomIdentityProfiles.form.placeholders.corpId')}
            value={formState.corpId}
          />
        </ProfileField>
        <ProfileField
          error={errors.agentId}
          hint={profile ? t('wecomIdentityProfiles.form.identityImmutable') : undefined}
          htmlFor="wecom-profile-agentId"
          label={t('wecomIdentityProfiles.form.fields.agentId')}
          required
        >
          <Input
            disabled={Boolean(profile)}
            id="wecom-profile-agentId"
            invalid={Boolean(errors.agentId)}
            onChange={(event) => updateValue('agentId', event.target.value)}
            placeholder={t('wecomIdentityProfiles.form.placeholders.agentId')}
            value={formState.agentId}
          />
        </ProfileField>
        <ProfileField
          error={errors.appSecret}
          hint={profile ? t('wecomIdentityProfiles.form.secretPreserveHint') : undefined}
          htmlFor="wecom-profile-appSecret"
          label={t('wecomIdentityProfiles.form.fields.appSecret')}
          required={!profile}
        >
          <PasswordInput
            hideLabel={t('wecomIdentityProfiles.form.hideSecret')}
            id="wecom-profile-appSecret"
            invalid={Boolean(errors.appSecret)}
            onChange={(event) => updateValue('appSecret', event.target.value)}
            placeholder={
              profile
                ? t('wecomIdentityProfiles.form.placeholders.secretEdit')
                : t('wecomIdentityProfiles.form.placeholders.secret')
            }
            showLabel={t('wecomIdentityProfiles.form.showSecret')}
            value={formState.appSecret}
          />
        </ProfileField>
        {profile ? <WecomEntryLink profileId={profile.id} /> : null}
        {validation ? <ProfileReadiness readiness={validation} /> : null}
      </div>
    </Modal>
  )
}

function ProfileReadiness({ readiness }: { readiness: WecomIdentityReadiness }) {
  const { t } = useTranslation('adminSettings')
  const actions = Object.entries(readiness.items)
    .filter(([, item]) => item.status !== 'ready')
    .map(([key]) =>
      t(`wecomIdentityProfiles.readiness.actions.${key}`, {
        defaultValue: t(`wecomIdentityProfiles.readiness.actionFallback.${readiness.status}`),
      }),
    )
  const visibleActions =
    actions.length > 0
      ? actions
      : readiness.status === 'ready'
        ? []
        : [t(`wecomIdentityProfiles.readiness.actionFallback.${readiness.status}`)]
  return (
    <InlineNotice
      tone={
        readiness.status === 'blocked'
          ? 'danger'
          : readiness.status === 'ready'
            ? 'success'
            : 'warning'
      }
    >
      <div className="flex items-center justify-between gap-3">
        <strong>{t('wecomIdentityProfiles.readiness.title')}</strong>
        <Badge variant={readinessVariant(readiness.status)}>
          {t(`wecomIdentityProfiles.readiness.status.${readiness.status}`)}
        </Badge>
      </div>
      <p className="mt-1">{t(`wecomIdentityProfiles.readiness.description.${readiness.status}`)}</p>
      {visibleActions.length > 0 ? (
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {visibleActions.map((action) => (
            <li key={action}>{action}</li>
          ))}
        </ul>
      ) : null}
    </InlineNotice>
  )
}

function ProfileField({
  children,
  error,
  hint,
  htmlFor,
  label,
  required = false,
}: {
  children: ReactNode
  error?: string
  hint?: string
  htmlFor: string
  label: string
  required?: boolean
}) {
  return (
    <FormField error={error} hint={hint} htmlFor={htmlFor} label={label} required={required}>
      {children}
    </FormField>
  )
}

function PlusIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
    </svg>
  )
}
